import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import DashboardNav from './DashboardNav';
import LoadingSpinner from './LoadingSpinner';
import './AttendanceReports.css';

const AttendanceReports = () => {
  const navigate = useNavigate();
  const [attendanceData, setAttendanceData] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    busId: '',
    format: 'pdf'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const fetchBuses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/buses', {
        headers: { 'x-auth-token': token }
      });
      const busesData = res.data?.data || res.data || [];
      setBuses(Array.isArray(busesData) ? busesData : []);
    } catch (err) {
      console.error('Error fetching buses:', err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/');
      }
      setBuses([]);
    }
  }, [navigate]);

  const fetchAttendanceData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });
      
      if (filters.startDate) {
        params.append('date', filters.startDate);
      }
      if (filters.busId) {
        params.append('bus', filters.busId);
      }
      
      const res = await api.get(`/attendance?${params.toString()}`, {
        headers: { 'x-auth-token': token }
      });
      
      setAttendanceData(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch (err) {
      console.error('Error fetching attendance data:', err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/');
      }
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.busId, pagination.limit, navigate]);

  useEffect(() => {
    fetchBuses();
    fetchAttendanceData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    fetchAttendanceData(1);
  };

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate,
        format: filters.format
      });
      
      if (filters.busId) {
        params.append('busId', filters.busId);
      }
      
      const response = await api.get(`/attendance/download?${params.toString()}`, {
        headers: { 'x-auth-token': token },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const fileExtension = filters.format === 'pdf' ? 'pdf' : 'csv';
      const fileName = `attendance-report-${filters.startDate}-to-${filters.endDate}.${fileExtension}`;
      link.setAttribute('download', fileName);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert(`${filters.format.toUpperCase()} report downloaded successfully!`);
    } catch (err) {
      console.error('Error downloading report:', err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/');
      }
      alert('Error downloading report. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    fetchAttendanceData(newPage);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  return (
    <div className="attendance-reports-container">
      <header className="school-header">
        <h1>School Portal</h1>
        <button className="logout-button" onClick={handleLogout}>Logout</button>
      </header>

      <DashboardNav />

      <main className="attendance-reports-content">
        <section className="filters-section">
          <h2>Attendance Reports & Downloads</h2>
          
          <div className="filters-form">
            <div className="filter-group">
              <label>Start Date:</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                required
              />
            </div>
            
            <div className="filter-group">
              <label>End Date:</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                required
              />
            </div>
            
            <div className="filter-group">
              <label>Bus (Optional):</label>
              <select name="busId" value={filters.busId} onChange={handleFilterChange}>
                <option value="">All Buses</option>
                {buses.map((bus) => (
                  <option key={bus._id} value={bus._id}>
                    {bus.busNumber}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Download Format:</label>
              <select name="format" value={filters.format} onChange={handleFilterChange}>
                <option value="pdf">PDF</option>
                <option value="csv">CSV</option>
              </select>
            </div>
            
            <div className="filter-actions">
              <button className="search-button" onClick={handleSearch} disabled={loading}>
                {loading ? 'Loading...' : 'Search'}
              </button>
              <button 
                className="download-button" 
                onClick={handleDownload} 
                disabled={downloadLoading || !filters.startDate || !filters.endDate}
              >
                {downloadLoading ? 'Downloading...' : `Download ${filters.format.toUpperCase()}`}
              </button>
            </div>
          </div>
        </section>

        <section className="attendance-table-section">
          <h3>Preview (Showing results for {filters.startDate})</h3>
          
          {loading ? (
            <LoadingSpinner size="medium" message="Loading attendance data..." />
          ) : (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>SL No.</th>
                      <th>Date & Time</th>
                      <th>Student Name</th>
                      <th>Student ID</th>
                      <th>Bus Number</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-data">
                          No attendance records found for the selected criteria.
                        </td>
                      </tr>
                    ) : (
                      attendanceData.map((record, index) => (
                        <tr key={record._id}>
                          <td>{((pagination.page - 1) * pagination.limit) + index + 1}</td>
                          <td>{new Date(record.timestamp).toLocaleString('en-IN', { 
                            timeZone: 'Asia/Kolkata',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}</td>
                          <td>{record.student.name}</td>
                          <td>{record.student.studentId}</td>
                          <td>{record.bus.busNumber}</td>
                          <td>
                            <span className={`status ${record.status}`}>
                              {record.status.toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrev}
                    className="page-button"
                  >
                    Previous
                  </button>
                  
                  <span className="page-info">
                    Page {pagination.page} of {pagination.totalPages} 
                    ({pagination.total} total records)
                  </span>
                  
                  <button 
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className="page-button"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
};

export default AttendanceReports;
